/**
 * Shared geometry for the stair plan fixtures. The four icons draw different
 * footprints but must agree on tread depth, flight width and arrowheads, so
 * those live here rather than being repeated per icon.
 */

/**
 * Tread depth as a fraction of the width of the flight it belongs to: a 0.25 m
 * going on a 1.0 m wide flight. Expressing it as a ratio rather than an
 * absolute length is what keeps the stairs free of the drawing scale — the
 * proportion holds whether the plan is 1:50 or 1:100, so nothing here has to
 * consult drawingHeadScale, and the toolbar can blow an icon up to its viewBox
 * and still get the step count of the fixture it stands for.
 */
export const TREAD_TO_FLIGHT_WIDTH = 0.25;

const MIN_STEPS = 3;
const MAX_STEPS = 40;

/**
 * Number of treads in a flight of the given length and width. A longer flight
 * gets more treads; a wider one gets deeper ones, in the proportion a real
 * stair has.
 */
export const stairStepCount = (flightLength: number, flightWidth: number) =>
  Math.min(
    MAX_STEPS,
    Math.max(
      MIN_STEPS,
      Math.round(flightLength / (TREAD_TO_FLIGHT_WIDTH * flightWidth))
    )
  );

/**
 * Width of one flight, and so the side of the square landing, for the corner
 * stair. Taken from the shorter side of the bounding box so that the two legs
 * of the L stay the same width whichever way the box is stretched.
 */
export const stairLegWidth = (width: number, height: number) =>
  Math.min(width, height) * 0.3;

export type ArrowDirection = 'up' | 'down' | 'left' | 'right';

/**
 * Path for the arrowhead at (x, y), drawn as an open V pointing `direction`.
 */
export const arrowHead = (
  x: number,
  y: number,
  direction: ArrowDirection,
  size: number
) => {
  const wing = size * 0.6;

  switch (direction) {
    case 'up':
      return `M${x - wing} ${y + size} L${x} ${y} L${x + wing} ${y + size}`;
    case 'down':
      return `M${x - wing} ${y - size} L${x} ${y} L${x + wing} ${y - size}`;
    case 'left':
      return `M${x + size} ${y - wing} L${x} ${y} L${x + size} ${y + wing}`;
    case 'right':
      return `M${x - size} ${y - wing} L${x} ${y} L${x - size} ${y + wing}`;
  }
};

export const arrowHeadSize = (flightWidth: number, flightLength: number) =>
  Math.max(2, Math.min(flightWidth * 0.18, flightLength * 0.18));

export const stairArrowInset = (flightWidth: number, flightLength: number) =>
  Math.min(flightWidth, flightLength) * 0.15;
